import { DOMSerializer, Fragment, type Node as PMNode, type Schema } from '@tiptap/pm/model';
import { EditorState } from '@tiptap/pm/state';

import { diffHtmlTree, type TContentHooks, type TDiffSide, type TDocDiffRuntime } from '@modules/documents/doc-engine';
import { ID_ATTR, LOADED_ID_ATTR, isNodeLoaded } from '@modules/documents/doc-engine/schema';
import {
  collectHeadings,
  createHeadingNumberingState,
  nextHeadingNumbering,
  numberCaptions,
  wrapTableFragmentHtml,
  type TTableFragmentTag,
} from '@confinion/confideck-shared';
import { collectCaptionPatches } from '@modules/documents/tiptap/nodes/caption/plugins/getAutoIndexCaptionsPlugin';
import { CAPTION_NODE_NAME } from '@modules/documents/tiptap/nodes/caption/constants';
import { applyAutoIndexPatches } from '@modules/documents/tiptap/plugins/shared';
import type { TAiEditPreview } from '@modules/documents/tiptap/plugins/AiEditReviewPlugin';
import type { THeading } from '@modules/documents/types';

import { groupAiEditSuggestions } from './aiEditPlan';
import { buildAiEditTransaction } from './aiEditTransaction';
import { EAiEditSuggestionKind, type TPlannedAiEdit } from './types';

const serializeNode = (node: PMNode, schema: Schema): { html: string; tag?: TTableFragmentTag } => {
  const container = document.createElement('div');
  container.appendChild(DOMSerializer.fromSchema(schema).serializeNode(node));
  return wrapTableFragmentHtml(container.innerHTML);
};

const serializeDocument = (node: PMNode, schema: Schema): string => {
  const container = document.createElement('div');
  container.appendChild(DOMSerializer.fromSchema(schema).serializeFragment(node.content));
  return container.innerHTML;
};

const resolvePreviewAtomAttrs = (
  attrs: Readonly<Record<string, string>>,
  side: TDiffSide,
  sources: ReadonlyMap<string, string>
): Readonly<Record<string, string>> => {
  if (side === 'previous') return attrs;
  const sourceId = sources.get(attrs[ID_ATTR]);
  return sourceId ? { ...attrs, [ID_ATTR]: sourceId, [LOADED_ID_ATTR]: sourceId } : attrs;
};

const resolvePreviewAtoms = (runtime: TDocDiffRuntime, sources: ReadonlyMap<string, string>): TDocDiffRuntime => ({
  ...runtime,
  // A copied atom is not registered under its new id yet, but the preview knows the placement it
  // still resolves through - so it renders its content instead of "Loading...".
  isNodeLoaded: (attrs, side) => (side === 'current' && sources.has(attrs[ID_ATTR]) ? true : isNodeLoaded(attrs)),
  resolveReference: (attrs, side) => runtime.resolveReference(resolvePreviewAtomAttrs(attrs, side, sources), side),
  getBlockInfo: (blockId, attrs, side) => {
    const sourceId = side === 'current' ? sources.get(blockId) : undefined;
    return runtime.getBlockInfo(sourceId ?? blockId, resolvePreviewAtomAttrs(attrs, side, sources), side);
  },
  renderBlockHeader: runtime.renderBlockHeader
    ? (attrs, side) => runtime.renderBlockHeader!(resolvePreviewAtomAttrs(attrs, side, sources), side)
    : undefined,
});

// The preview is never dispatched, so the live auto-index plugin never numbers it - its rule runs here.
const renumberCaptions = (doc: PMNode): PMNode => {
  const state = EditorState.create({ doc });
  const patches = collectCaptionPatches(state);
  return patches.length ? applyAutoIndexPatches(state.tr, patches).doc : doc;
};

const buildCaptionIndexById = (renumberedDoc: PMNode): ReadonlyMap<string, number> =>
  new Map(
    numberCaptions(renumberedDoc).flatMap(({ node, index }) =>
      typeof node.attrs.id === 'string' ? [[node.attrs.id, index] as const] : []
    )
  );

// A suggestion's node is serialized on its own, outside the renumbered document, so its captions get the numbers copied in.
const applyCaptionIndices = (node: PMNode, indexById: ReadonlyMap<string, number>): PMNode => {
  if (node.isText) return node;
  const wanted =
    node.type.name === CAPTION_NODE_NAME && typeof node.attrs.id === 'string'
      ? indexById.get(node.attrs.id)
      : undefined;
  const attrs = wanted !== undefined && wanted !== node.attrs.index ? { ...node.attrs, index: wanted } : node.attrs;

  let childrenChanged = false;
  const children: PMNode[] = [];
  node.content.forEach((child) => {
    const patched = applyCaptionIndices(child, indexById);
    if (patched !== child) childrenChanged = true;
    children.push(patched);
  });

  if (attrs === node.attrs && !childrenChanged) return node;
  return node.type.create(attrs, childrenChanged ? Fragment.fromArray(children) : node.content, node.marks);
};

type TAiEditPreviewDocument = {
  html: string;
  headings: THeading[];
};

export type TAiEditPreviewRuntimeInput = {
  current: TAiEditPreviewDocument;
  previous: TAiEditPreviewDocument;
};

export type TAiEditPreviewOptions = {
  createRuntime: (input: TAiEditPreviewRuntimeInput) => TDocDiffRuntime;
  hooks: TContentHooks;
  headingStart?: number;
  isH5RefEnabled?: boolean;
  preprocessHtml: (html: string) => string;
};

const numberPreviewHeadings = (doc: PMNode, headingStart: number): THeading[] => {
  const state = createHeadingNumberingState();
  return collectHeadings(doc).map(({ node }) => ({
    id: node.attrs.id,
    level: node.attrs.level,
    text: node.textContent.trim(),
    numbering: nextHeadingNumbering(state, node.attrs.level, headingStart),
  }));
};

const getPreviewDocument = (doc: PMNode, schema: Schema, headingStart: number): TAiEditPreviewDocument => ({
  html: serializeDocument(doc, schema),
  headings: numberPreviewHeadings(doc, headingStart),
});

export const buildAiEditPreview = async (
  plan: TPlannedAiEdit,
  schema: Schema,
  options: TAiEditPreviewOptions
): Promise<TAiEditPreview> => {
  // Own state, so the transaction below cannot reach the live editor. It does not copy the document.
  const previewState = EditorState.create({ doc: plan.baselineDoc });
  const previewDoc = renumberCaptions(buildAiEditTransaction(previewState, plan).doc);
  const captionIndexById = buildCaptionIndexById(previewDoc);
  const previewAtomSources = plan.previewAtomSources;
  const headingStart = options.headingStart ?? 1;
  const runtime = resolvePreviewAtoms(
    options.createRuntime({
      current: getPreviewDocument(previewDoc, schema, headingStart),
      previous: getPreviewDocument(plan.baselineDoc, schema, headingStart),
    }),
    previewAtomSources
  );
  // Rendered order: the review plugin ranks widgets that share a position by their index here.
  const placed = groupAiEditSuggestions(plan.suggestions).flatMap((group) => [
    ...group.before.map((suggestion) => ({ group, suggestion, placement: EAiEditSuggestionKind.INSERT_BEFORE })),
    ...group.destructive.map((suggestion) => ({ group, suggestion, placement: suggestion.kind })),
    ...group.after.map((suggestion) => ({ group, suggestion, placement: EAiEditSuggestionKind.INSERT_AFTER })),
  ]);
  const suggestions = await Promise.all(
    placed.map(async ({ group, suggestion, placement }) => {
      const base = {
        id: suggestion.id,
        savedElementId: group.elementId,
        placement,
        target: { pos: group.target.pos, end: group.target.end },
      };
      if (suggestion.kind === EAiEditSuggestionKind.REMOVE) return base;

      const serializedCurrent = serializeNode(applyCaptionIndices(suggestion.node, captionIndexById), schema);
      const serializedPreviousHtml =
        suggestion.kind === EAiEditSuggestionKind.REPLACE ? serializeNode(group.target.node, schema).html : '';
      const currentHtml = options.preprocessHtml(serializedCurrent.html);
      const previousHtml = options.preprocessHtml(serializedPreviousHtml);
      const { html } = await diffHtmlTree(previousHtml, currentHtml, {
        runtime,
        opts: { isH5RefEnabled: options.isH5RefEnabled },
        hooks: options.hooks,
      });
      // A div inside a paragraph would break its line around an inline target.
      return {
        ...base,
        html,
        widgetTag: serializedCurrent.tag ?? (group.target.node.isInline ? ('span' as const) : undefined),
      };
    })
  );
  return { suggestions };
};

export const getAiEditNavigationElementIds = (plan: TPlannedAiEdit): string[] =>
  groupAiEditSuggestions(plan.suggestions)
    .sort((left, right) => left.target.pos - right.target.pos)
    .map(({ elementId }) => elementId);
